import AppService from "./AppService";
import api from "./api";

/**
 * Service for managing organization configuration API calls
 */
class OrganizationConfigService {
  /**
   * Helper method to handle API errors
   * @param {Error} error The error object
   * @param {string} operation The operation being performed
   * @returns {Promise} Rejected promise with the error
   */
  handleError(error, operation) {
    console.error(`OrganizationConfigService: ${operation} operation failed:`, error);
    console.error(`Error details:`, error?.response?.data || 'No response data');
    console.error(`Status code:`, error?.response?.status || 'No status code');
    
    // Check for authentication errors
    if (error?.response?.status === 401) {
      console.error('Authentication error - token may be invalid or expired');
      
      // If we have an auth token, log details about it
      const token = localStorage.getItem('jwt_token') || 
                   (localStorage.getItem('authInfo') ? 
                    JSON.parse(localStorage.getItem('authInfo'))?.token : null);
      
      if (token) {
        try {
          // Try to decode the token to check expiration
          const tokenParts = token.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            const now = Math.floor(Date.now() / 1000);
            console.error(`Token expired: ${payload.exp < now}`);
            console.error(`Token expiration: ${new Date(payload.exp * 1000).toLocaleString()}`);
            console.error(`Current time: ${new Date().toLocaleString()}`);
          }
        } catch (e) {
          console.error('Error analyzing token:', e);
        }
      } else {
        console.error('No auth token found');
      }
    }
    
    return Promise.reject(error);
  }
  /**
   * Get all organization configurations
   * @returns {Promise} Promise with the list of organization configurations
   */
  getAllOrganizationConfigs() {
    return AppService.get("/api/organization-config")
      .catch(error => this.handleError(error, 'getAllOrganizationConfigs'));
  }

  /**
   * Get organization configuration by ID
   * @param {string} id Configuration ID
   * @returns {Promise} Promise with the organization configuration
   */
  getOrganizationConfigById(id) {
    return AppService.get(`/api/organization-config/${id}`)
      .catch(error => this.handleError(error, 'getOrganizationConfigById'));
  }

  /**
   * Get organization configuration by organization ID
   * @param {string} organizationId Organization ID
   * @returns {Promise} Promise with the organization configuration
   */
  getOrganizationConfigByOrganizationId(organizationId) {
    return AppService.get(`/api/organization-config/organization/${organizationId}`)
      .catch(error => this.handleError(error, 'getOrganizationConfigByOrganizationId'));
  }

  /**
   * Create organization configuration
   * @param {Object} configData Configuration data
   * @returns {Promise} Promise with the created configuration
   */
  createOrganizationConfig(configData) {
    return AppService.post("/api/organization-config", configData)
      .catch(error => this.handleError(error, 'createOrganizationConfig'));
  }

  /**
   * Update organization configuration
   * @param {string} id Configuration ID
   * @param {Object} configData Updated configuration data
   * @returns {Promise} Promise with the updated configuration
   */
  updateOrganizationConfig(id, configData) {
    return AppService.put(`/api/organization-config/${id}`, configData)
      .catch(error => this.handleError(error, 'updateOrganizationConfig'));
  }

  /**
   * Upsert organization configuration by organizationId (server will create if missing)
   * @param {string} organizationId Organization ID (UUID)
   * @param {Object} configData Configuration data
   */
  updateByOrganizationId(organizationId, configData) {
    return AppService.put(`/api/organization-config/organization/${organizationId}`, configData)
      .catch(error => this.handleError(error, 'updateByOrganizationId'));
  }

  /**
   * Delete organization configuration
   * @param {string} id Configuration ID
   * @returns {Promise} Promise with deletion status
   */
  deleteOrganizationConfig(id) {
    return AppService.delete(`/api/organization-config/${id}`)
      .catch(error => this.handleError(error, 'deleteOrganizationConfig'));
  }

  // ============= Organization Logo ============= //
  /**
   * Fetch organization logo as ArrayBuffer for preview
   * @param {string} organizationId
   * @returns {Promise<ArrayBuffer>}
   */
  getOrganizationLogo(organizationId) {
    // Use axios instance directly to pass responseType config
    const url = `/api/organizations/${organizationId}/logo`;
    return api.api.get(url, { responseType: 'arraybuffer' })
      .then(res => res.data)
      .catch(error => this.handleError(error, 'getOrganizationLogo'));
  }

  /**
   * Upload organization logo (multipart/form-data)
   * @param {string} organizationId
   * @param {File} file
   * @returns {Promise}
   */
  uploadOrganizationLogo(organizationId, file) {
    const formData = new FormData();
    formData.append('file', file);
    const url = `/api/organizations/${organizationId}/logo`;
    // Use axios instance and set multipart header so Spring sees multipart request
    return api.api.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(res => res.data)
      .catch(error => this.handleError(error, 'uploadOrganizationLogo'));
  }

  /**
   * Delete organization logo
   * @param {string} organizationId
   * @returns {Promise}
   */
  deleteOrganizationLogo(organizationId) {
    return AppService.delete(`/api/organizations/${organizationId}/logo`)
      .catch(error => this.handleError(error, 'deleteOrganizationLogo'));
  }

  /**
   * Update notification settings by organizationId
   * @param {string} organizationId Organization ID (UUID)
   * @param {Object} notificationData Notification channels and events data
   * @returns {Promise}
   */
  updateNotificationSettings(organizationId, notificationData) {
    return AppService.put(`/api/organization-config/notification/organization/${organizationId}`, notificationData)
      .catch(error => this.handleError(error, 'updateNotificationSettings'));
  }
}

const organizationConfigService = new OrganizationConfigService();
export default organizationConfigService;
